import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { FormGroup, Label, Input, Button, Spinner } from "reactstrap";
import Select from "react-select";

import api from "../../../../services/api";
import { useCompanies } from "../../../../hooks/companies";

const selectStyles = {
  menu: (base) => ({
    ...base,
    zIndex: 30,
    background: "var(--everlog-card-surface-flat)",
    border: "1px solid var(--everlog-card-border)",
    borderRadius: 14,
    boxShadow: "var(--everlog-card-shadow)",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 50 }),
  control: (base, state) => ({
    ...base,
    background: "var(--everlog-card-surface-field)",
    backgroundColor: "var(--everlog-card-surface-field)",
    borderColor: state.isFocused
      ? "var(--theme-accent)"
      : "var(--everlog-card-border)",
    borderRadius: 14,
    minHeight: 42,
    opacity: state.isDisabled ? 0.78 : 1,
    boxShadow: state.isFocused ? "0 0 0 3px var(--theme-accent-glow)" : "none",
    transition: "border-color 0.18s ease, box-shadow 0.18s ease",
  }),
  valueContainer: (base) => ({ ...base, padding: "2px 10px" }),
  multiValue: (base) => ({
    ...base,
    background: "var(--everlog-card-surface-pill)",
    border: "1px solid var(--everlog-card-border-faint)",
    borderRadius: 999,
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "var(--everlog-card-title)",
    fontWeight: 600,
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "var(--everlog-card-muted)",
    borderRadius: 999,
    ":hover": {
      background: "var(--everlog-card-surface-active)",
      color: "var(--everlog-card-title)",
    },
  }),
  singleValue: (base) => ({
    ...base,
    color: "var(--everlog-card-title)",
  }),
  input: (base) => ({
    ...base,
    color: "var(--everlog-card-text)",
  }),
  placeholder: (base) => ({
    ...base,
    color: "var(--everlog-card-muted)",
  }),
  option: (base, state) => ({
    ...base,
    background: state.isFocused
      ? "var(--everlog-card-interactive-bg)"
      : state.isSelected
      ? "var(--everlog-card-surface-active)"
      : "var(--everlog-card-surface-flat)",
    color: "var(--everlog-card-text)",
    cursor: "pointer",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: "var(--everlog-card-muted)",
  }),
  clearIndicator: (base) => ({
    ...base,
    color: "var(--everlog-card-muted)",
  }),
  indicatorSeparator: (base) => ({
    ...base,
    backgroundColor: "var(--everlog-card-border)",
  }),
};

const QUICK_RANGES = [
  { key: "month", label: "Mês atual", days: null },
  { key: "7d", label: "7 dias", days: 6 },
  { key: "30d", label: "30 dias", days: 29 },
  { key: "90d", label: "90 dias", days: 89 },
];

const getQuickRangeDates = (rangeKey) => {
  const today = moment();

  if (rangeKey === "month") {
    return {
      dataDe: today.clone().startOf("month").format("YYYY-MM-DD"),
      dataAte: today.format("YYYY-MM-DD"),
    };
  }

  const range = QUICK_RANGES.find((item) => item.key === rangeKey);
  const days = range && range.days !== null ? range.days : 29;

  return {
    dataDe: today.clone().subtract(days, "days").format("YYYY-MM-DD"),
    dataAte: today.format("YYYY-MM-DD"),
  };
};

const createDefaultDates = () => getQuickRangeDates("30d");

// onSubmit(payload, rotulos): `payload` é o que vai para a API (não mudou);
// `rotulos` (PO1-3632) são os nomes dos filtros aplicados, para a linha de
// filtro do card PNG ("Transportadora: X").
function FaturamentoFiltros({ onSubmit, loading }) {
  const { empresaId, transportadoraId, transportadora } = useCompanies();
  const currentEmpresaKey = empresaId || null;
  const submittedInitialRef = useRef(false);
  const selectMenuPortalTarget =
    typeof document !== "undefined" ? document.body : null;

  const defaultDates = createDefaultDates();
  const [dataDe, setDataDe] = useState(defaultDates.dataDe);
  const [dataAte, setDataAte] = useState(defaultDates.dataAte);
  const [modais, setModais] = useState([]);
  const [servicos, setServicos] = useState([]);
  const [negocios, setNegocios] = useState([]);
  const [estados, setEstados] = useState([]);
  const [regioes, setRegioes] = useState([]);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const [opts, setOpts] = useState({
    modais: [],
    servicos: [],
    negocios: [],
    estados: [],
    regioes: [],
  });
  const [optsEmpresaId, setOptsEmpresaId] = useState(null);
  const [loadingOpts, setLoadingOpts] = useState(true);
  const [errorOpts, setErrorOpts] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const requestEmpresaKey = currentEmpresaKey;

    async function fetchAll() {
      setLoadingOpts(true);
      setErrorOpts(null);
      try {
        const [mRes, sRes, eRes, rRes, nRes] = await Promise.all([
          api.get("/frete/modais"),
          api.get("/frete/tipos-servico"),
          api.get("/frete/estados"),
          api.get("/frete/regioes-frete"),
          requestEmpresaKey
            ? api.get(`/embarcadores/${requestEmpresaKey}/negocios`)
            : Promise.resolve({ data: [] }),
        ]);
        if (cancelled) return;
        setOpts({
          modais: (mRes.data || []).map((x) => ({
            value: x.id,
            label: x.nome,
          })),
          servicos: (sRes.data || []).map((x) => ({
            value: x.nome,
            label: x.nome,
          })),
          estados: (eRes.data || []).map((x) => ({
            value: x.id,
            label: `${x.uf} - ${x.nome}`,
          })),
          regioes: (rRes.data || []).map((x) => ({
            value: x.id,
            label: x.nome,
          })),
          negocios: (nRes.data || []).map((x) => ({
            value: x.id,
            label: x.descricao || x.nome,
          })),
        });
        setOptsEmpresaId(requestEmpresaKey);
      } catch (e) {
        if (cancelled) return;
        setErrorOpts(
          (e && e.response && e.response.data && e.response.data.error) ||
            "Erro ao carregar opções dos filtros"
        );
        setOptsEmpresaId(requestEmpresaKey);
      } finally {
        if (!cancelled) setLoadingOpts(false);
      }
    }

    fetchAll();
    return () => {
      cancelled = true;
    };
  }, [currentEmpresaKey]);

  useEffect(() => {
    setNegocios([]);
  }, [currentEmpresaKey]);

  const validationError = useMemo(() => {
    if (!dataDe || !dataAte) return "Informe a data de início e de fim.";
    const de = moment(dataDe, "YYYY-MM-DD", true);
    const ate = moment(dataAte, "YYYY-MM-DD", true);
    if (!de.isValid() || !ate.isValid()) return "Datas inválidas.";
    if (ate.isBefore(de))
      return "A data final deve ser maior ou igual à inicial.";
    return null;
  }, [dataDe, dataAte]);

  const activeQuickRange = useMemo(() => {
    const active = QUICK_RANGES.find((range) => {
      const dates = getQuickRangeDates(range.key);
      return dates.dataDe === dataDe && dates.dataAte === dataAte;
    });
    return active ? active.key : null;
  }, [dataDe, dataAte]);

  const advancedFiltersCount = useMemo(
    () =>
      [modais, servicos, negocios, estados, regioes].reduce(
        (total, values) => total + (values.length > 0 ? 1 : 0),
        0
      ),
    [estados, modais, negocios, regioes, servicos]
  );

  const optionsReady = !loadingOpts && optsEmpresaId === currentEmpresaKey;
  const canSubmit = !validationError && !loading && optionsReady;

  const buildPayload = useCallback(
    ({
      nextDataDe = dataDe,
      nextDataAte = dataAte,
      nextModais = modais,
      nextServicos = servicos,
      nextNegocios = negocios,
      nextEstados = estados,
      nextRegioes = regioes,
    } = {}) => ({
      dataExpedicaoDe: nextDataDe,
      dataExpedicaoAte: nextDataAte,
      idsModal: nextModais.map((m) => m.value),
      servicos: nextServicos.map((s) => s.value),
      idsNegocio: nextNegocios.map((n) => n.value),
      idsEstado: nextEstados.map((e) => e.value),
      idsRegiaoFrete: nextRegioes.map((r) => r.value),
      idTransportadora: transportadoraId || undefined,
    }),
    [
      dataDe,
      dataAte,
      modais,
      servicos,
      negocios,
      estados,
      regioes,
      transportadoraId,
    ]
  );

  const buildRotulos = useCallback(
    ({ nextNegocios = negocios, nextEstados = estados, nextRegioes = regioes } = {}) => ({
      transportadora: transportadoraId && transportadora ? transportadora.label || null : null,
      negocios: nextNegocios.map((n) => n.label),
      estados: nextEstados.map((e) => e.label),
      regioes: nextRegioes.map((r) => r.label),
    }),
    [negocios, estados, regioes, transportadoraId, transportadora]
  );

  const handleSubmit = useCallback(() => {
    if (!canSubmit) return;
    onSubmit(buildPayload(), buildRotulos());
  }, [buildPayload, buildRotulos, canSubmit, onSubmit]);

  const applyQuickRange = useCallback((rangeKey) => {
    const dates = getQuickRangeDates(rangeKey);
    setDataDe(dates.dataDe);
    setDataAte(dates.dataAte);
  }, []);

  const resetFilters = useCallback(() => {
    const dates = createDefaultDates();
    setDataDe(dates.dataDe);
    setDataAte(dates.dataAte);
    setModais([]);
    setServicos([]);
    setNegocios([]);
    setEstados([]);
    setRegioes([]);
    if (optionsReady && !loading) {
      onSubmit(
        buildPayload({
          nextDataDe: dates.dataDe,
          nextDataAte: dates.dataAte,
          nextModais: [],
          nextServicos: [],
          nextNegocios: [],
          nextEstados: [],
          nextRegioes: [],
        }),
        buildRotulos({ nextNegocios: [], nextEstados: [], nextRegioes: [] })
      );
    }
  }, [buildPayload, buildRotulos, loading, onSubmit, optionsReady]);

  useEffect(() => {
    submittedInitialRef.current = false;
  }, [empresaId, transportadoraId]);

  useEffect(() => {
    if (submittedInitialRef.current || !canSubmit) return;
    submittedInitialRef.current = true;
    handleSubmit();
  }, [canSubmit, handleSubmit]);

  return (
    <div className="faturamento-filtros">
      <div className="faturamento-filter-main">
        <FormGroup className="faturamento-filter-main__ranges">
          <Label>Período</Label>
          <div className="faturamento-quick-range">
            {QUICK_RANGES.map((range) => (
              <button
                key={range.key}
                type="button"
                className={`faturamento-quick-range__button ${
                  activeQuickRange === range.key ? "is-active" : ""
                }`}
                onClick={() => applyQuickRange(range.key)}
                disabled={loading}
              >
                {range.label}
              </button>
            ))}
          </div>
        </FormGroup>

        <FormGroup>
          <Label>Data De</Label>
          <Input
            type="date"
            value={dataDe}
            onChange={(e) => setDataDe(e.target.value)}
            disabled={loading}
          />
        </FormGroup>

        <FormGroup>
          <Label>Data Até</Label>
          <Input
            type="date"
            value={dataAte}
            onChange={(e) => setDataAte(e.target.value)}
            disabled={loading}
          />
        </FormGroup>

        <div className="faturamento-filter-main__actions">
          <Button
            type="button"
            className={`faturamento-button-secondary faturamento-filter-toggle ${
              showAdvancedFilters ? "is-open" : ""
            }`}
            onClick={() => setShowAdvancedFilters((current) => !current)}
            aria-expanded={showAdvancedFilters}
            aria-controls="faturamento-advanced-filters"
          >
            <i className="tim-icons icon-settings-gear-63" />
            Filtros
            {advancedFiltersCount > 0 && (
              <span className="faturamento-filter-toggle__badge">
                {advancedFiltersCount}
              </span>
            )}
          </Button>

          <Button
            type="button"
            className="faturamento-button-primary faturamento-filter-refresh"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {loading ? <Spinner size="sm" /> : "Atualizar indicador"}
          </Button>
        </div>
      </div>

      {showAdvancedFilters && (
        <div
          className="faturamento-advanced-filters"
          id="faturamento-advanced-filters"
        >
          <div className="faturamento-advanced-filters__grid">
            <FormGroup>
              <Label>Modal</Label>
              <Select
                isMulti
                className="react-select info"
                classNamePrefix="react-select"
                options={opts.modais}
                value={modais}
                onChange={(v) => setModais(v || [])}
                isDisabled={loading || loadingOpts}
                styles={selectStyles}
                placeholder="Todos"
                noOptionsMessage={() => "Sem opções"}
                menuPortalTarget={selectMenuPortalTarget}
              />
            </FormGroup>

            <FormGroup>
              <Label>Serviço</Label>
              <Select
                isMulti
                className="react-select info"
                classNamePrefix="react-select"
                options={opts.servicos}
                value={servicos}
                onChange={(v) => setServicos(v || [])}
                isDisabled={loading || loadingOpts}
                styles={selectStyles}
                placeholder="Todos"
                noOptionsMessage={() => "Sem opções"}
                menuPortalTarget={selectMenuPortalTarget}
              />
            </FormGroup>

            <FormGroup>
              <Label>Negócio</Label>
              <Select
                isMulti
                className="react-select info"
                classNamePrefix="react-select"
                options={opts.negocios}
                value={negocios}
                onChange={(v) => setNegocios(v || [])}
                isDisabled={loading || loadingOpts}
                styles={selectStyles}
                placeholder="Todos"
                noOptionsMessage={() => "Sem opções"}
                menuPortalTarget={selectMenuPortalTarget}
              />
            </FormGroup>

            <FormGroup>
              <Label>UF</Label>
              <Select
                isMulti
                className="react-select info"
                classNamePrefix="react-select"
                options={opts.estados}
                value={estados}
                onChange={(v) => setEstados(v || [])}
                isDisabled={loading || loadingOpts}
                styles={selectStyles}
                placeholder="Todos"
                noOptionsMessage={() => "Sem opções"}
                menuPortalTarget={selectMenuPortalTarget}
              />
            </FormGroup>

            <FormGroup>
              <Label>Região</Label>
              <Select
                isMulti
                className="react-select info"
                classNamePrefix="react-select"
                options={opts.regioes}
                value={regioes}
                onChange={(v) => setRegioes(v || [])}
                isDisabled={loading || loadingOpts}
                styles={selectStyles}
                placeholder="Todas"
                noOptionsMessage={() => "Sem opções"}
                menuPortalTarget={selectMenuPortalTarget}
              />
            </FormGroup>
          </div>

          <div className="faturamento-advanced-filters__actions">
            <Button
              type="button"
              className="faturamento-button-secondary"
              onClick={resetFilters}
              disabled={loading}
            >
              Limpar filtros
            </Button>
          </div>
        </div>
      )}
      {(errorOpts || (validationError && !loadingOpts)) && (
        <div className="faturamento-filtros-msg">
          {errorOpts && (
            <div className="faturamento-filtros-msg-err">{errorOpts}</div>
          )}
          {validationError && !loadingOpts && (
            <div className="faturamento-filtros-msg-warn">
              {validationError}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default FaturamentoFiltros;
